<template>
  <v-row justify="center">
    <v-dialog
      v-model="dialog"
      :fullscreen="$vuetify.breakpoint.xsOnly"
      max-width="800"
      persistent
      scrollable
    >
      <v-card
        class="d-flex flex-column"
        max-height="80vh"
      >
        <v-app-bar
          color="cyan"
          dark
          flat
        >
          <v-toolbar-title class="text-h5">
            Tag a Party
          </v-toolbar-title>

          <v-spacer />

          <v-btn
            icon
            @click="onCancelTagParty"
          >
            <v-icon>close</v-icon>
          </v-btn>
        </v-app-bar>

        <v-card-text>
          <v-sheet
            rounded
            class="pt-3"
          >
            <v-row>
              <v-col>
                <v-autocomplete
                  v-model="party.tags"
                  chips
                  deletable-chips
                  hide-selected
                  outlined
                  multiple
                  :error-messages="getErrorMessages($v.party.tags)"
                  :items="availableOptionsTags"
                  @input="$v.party.tags.$touch()"
                  @blur="$v.party.tags.$touch()"
                >
                  <template #selection="{ item }">
                    <v-chip
                      close
                      label
                      :color="item.color"
                      @click:close="removeTagSelection(item.value)"
                    >
                      {{ item.text }}
                    </v-chip>
                  </template>
                </v-autocomplete>
              </v-col>
            </v-row>
          </v-sheet>
        </v-card-text>

        <v-divider />

        <v-card-actions>
          <v-spacer />

          <v-btn
            color="secondary"
            text
            @click="onCancelTagParty"
          >
            cancel
          </v-btn>

          <v-btn
            :disabled="!canTagParty"
            color="primary"
            @click="confirmationDialog = !confirmationDialog"
          >
            Tag Party
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <ConfirmationDialog
      cancel-button-color="secondary darken-1"
      confirm-button-color="primary darken-1"
      title="Tag Party"
      :visible="confirmationDialog"
      @canceled="onCancelTagParty"
      @confirmed="onTagParty"
    />
  </v-row>
</template>

<script>
import { mapActions, mapGetters, mapMutations } from 'vuex'
import ConfirmationDialog from '@/components/Dialogs/Common/ConfirmationDialog.vue'
import { helpers } from '@vuelidate/validators'
import { useVuelidate } from '@vuelidate/core'

import { PARTY_NAMESPACE } from '@/store/party'
import { SHARED_NAMESPACE } from '@/store/shared'

import { alertHandler } from '@/alertHandler'

const asyncValidator = (selectedTags, _, dis) => {
  return (dis.peParty.tags.length + selectedTags.length) <= 5
}

export default {
  name: 'TagParty',

  components: {
    ConfirmationDialog,
  },

  setup () {
    return {
      $v: useVuelidate(),
      ...alertHandler(),
    }
  },

  data () {
    return {
      confirmationDialog: false,
      party: {
        tags: [],
      },
    }
  },

  validations () {
    return {
      party: {
        tags: {
          asyncValidator: helpers.withMessage('Only 5 tags allowed per party', helpers.withAsync(asyncValidator)),
        },
      },
    }
  },

  computed: {
    availableOptionsTags () {
      const partyTags = this.peParty?.tags?.map(item => item.id)
      const availableTags = this.optionsTags?.filter(item => !partyTags?.includes(item.value))

      return availableTags
    },

    canTagParty () {
      return this.party.tags.length > 0 && (this.peParty?.tags?.length + this.party?.tags?.length) <= 5
    },

    dialog: {
      get () { return this.showDialogPartyTag },

      set (val) {
        this.setShowTagPartyDialog(val)
      },
    },

    optionsTags () { return this.getCachedDropdown('tags') },

    ...mapGetters(PARTY_NAMESPACE, [
      'peParty',
      'showDialogPartyTag',
    ]),

    ...mapGetters(SHARED_NAMESPACE, [
      'getCachedDropdown',
    ]),
  },

  created () {
    this.getDropdownOptions({ force: true, options: false, path: 'tags' })
  },

  methods: {
    getErrorMessages (validator) { return validator?.$errors.map(e => e.$message) },

    onCancelTagParty () {
      this.confirmationDialog = false
      this.dialog = false
      this.party.tags = []
    },

    onTagParty () {
      this.tagParty({ id: this.peParty.id, tagIds: this.party.tags }).
        then(_ => {
          this.confirmationDialog = false
          this.dialog = false
          this.party.tags = []
        }).
        catch(({ data }) => {
          this.displayAlert('warning', data.notice || 'Internal Server Error')
        })
    },

    removeTagSelection (value) {
      this.party.tags = this.party.tags.filter(el => el !== value)
    },

    ...mapActions(PARTY_NAMESPACE, [
      'tagParty',
    ]),

    ...mapActions(SHARED_NAMESPACE, [
      'getDropdownOptions',
    ]),

    ...mapMutations(PARTY_NAMESPACE, [
      'setShowTagPartyDialog',
    ]),
  },
}
</script>
